import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { IconButton, InputAdornment } from "@mui/material";
import { ArrowBack, Search } from "@mui/icons-material";
import moment from "moment";
import errorHandler from "../../utils/errorHandler";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import AppTextField from "../../lib/components/AppTextField";
import AppSelectField from "../../lib/components/AppSelectField";
import AppExportButton from "../../lib/components/AppExportButton";
import DataTable from "../../lib/components/DataTable";
import useDebounce from "../../hooks/useDebounce";
import promoCodeService from "../../service/promoCodeService";
import pathnames from "../../constants/pathnames";
import { moneyFormat } from "../../utils/moneyFormat";
import PromoStatusBadge from "./components/promo-status-badge";

const PLAN_FILTER_OPTIONS = [
  { label: "All Plans", value: "ALL" },
  { label: "Simple Start", value: "Simple Start" },
  { label: "Standard", value: "Standard" },
  { label: "Premium", value: "Premium" },
  { label: "Enterprise", value: "Enterprise" },
];

const PLATFORM_FILTER_OPTIONS = [
  { label: "All Platforms", value: "ALL" },
  { label: "Web", value: "Web" },
  { label: "iOS", value: "iOS" },
  { label: "Android", value: "Android" },
];

const STATUS_FILTER_OPTIONS = [
  { label: "All Confirmed", value: "ALL" },
  { label: "Active", value: "ACTIVE" },
  { label: "Trial", value: "TRIAL" },
  { label: "Pending payment", value: "RESERVED" },
  { label: "Released", value: "RELEASED" },
];

const PLATFORM_ICONS = {
  web: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  ios: (
    <svg width="12" height="14" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  ),
  android: (
    <svg width="14" height="14" viewBox="0 0 576 512" fill="currentColor" aria-hidden="true">
      <path d="M420.55,301.93a24,24,0,1,1,24-24,24,24,0,0,1-24,24m-265.1,0a24,24,0,1,1,24-24,24,24,0,0,1-24,24m273.7-144.48,47.94-83a10,10,0,1,0-17.27-10h0l-48.54,84.07a301.25,301.25,0,0,0-246.56,0L116.18,64.45a10,10,0,1,0-17.27,10h0l47.94,83C64.53,202.22,8.24,285.55,0,384H576c-8.24-98.45-64.54-181.78-146.85-226.55" />
    </svg>
  ),
};

const PLATFORM_LABELS = { web: "Web", ios: "iOS", android: "Android" };

const renderPlatform = (platform) => {
  if (!platform) return "N/A";
  const key = String(platform).toLowerCase();

  return (
    <span className="d-inline-flex align-items-center gap-2 text-nowrap">
      {PLATFORM_ICONS[key]}
      {PLATFORM_LABELS[key] || platform}
    </span>
  );
};

const columns = [
  {
    key: "customer",
    header: "Customer & Company",
    render: (_, row) => {
      const company = row.customer?.company || row.companyName || "";
      const email = row.customer?.email || row.email || "";
      return (
        <div>
          <div className="fw-medium">{row.customer?.name || row.customerName || "Customer"}</div>
          <div className="fs-sm text-secondary">
            {company ? `${company} • ` : ""}
            {email}
          </div>
        </div>
      );
    },
  },
  { key: "code", header: "Promo Code", render: (_, row) => <span className="fw-medium">{row.code || row.promoCode || "N/A"}</span> },
  {
    key: "plan",
    header: "Plan & Product",
    render: (_, row) => (
      <div>
        <div>{row.productName || row.planTier || "Standard Tier"}</div>
        {row.product && <div className="fs-sm text-secondary">{row.product}</div>}
      </div>
    ),
  },
  { key: "platform", header: "Platform", render: (value) => renderPlatform(value) },
  { key: "originalAmount", header: "Original", render: (_, row) => moneyFormat(row.originalAmount ?? row.original ?? 0, "NGN") },
  {
    key: "discountAmount",
    header: "Discount",
    render: (_, row) => <span className="text-success">-{moneyFormat(row.discountAmount ?? row.discount ?? 0, "NGN")}</span>,
  },
  {
    key: "finalAmount",
    header: "Final Paid",
    render: (_, row) => <span className="fw-medium">{moneyFormat(row.finalAmount ?? row.finalPaid ?? 0, "NGN")}</span>,
  },
  {
    key: "redeemedAt",
    header: "Date Redeemed (WAT)",
    render: (value, row) => (
      <span className="text-nowrap">{value ? moment(value).format("DD-MM-YYYY HH:mm") : row.dateRedeemed || "N/A"}</span>
    ),
  },
  { key: "status", header: "Status", render: (value) => <PromoStatusBadge status={value} /> },
];

export default function RedemptionsHistory() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("ALL");
  const [selectedPlatform, setSelectedPlatform] = useState("ALL");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const debouncedSearch = useDebounce(searchTerm);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(pathnames.PROMO_CODES_OVERVIEW);
    }
  };

  const filters = {
    plan: selectedPlan !== "ALL" ? selectedPlan : undefined,
    platform: selectedPlatform !== "ALL" ? selectedPlatform.toUpperCase() : undefined,
    status: selectedStatus !== "ALL" ? selectedStatus.toUpperCase() : undefined,
  };

  const { data: redemptionsData, isPending: isLoadingRedemptions } = useQuery({
    queryKey: [
      "promo-codes-redemptions",
      debouncedSearch,
      selectedPlan,
      selectedPlatform,
      selectedStatus,
      page,
      limit,
    ],
    queryFn: () =>
      promoCodeService.getRedemptions({
        keyword: debouncedSearch,
        ...filters,
        page,
        limit,
      }),
  });

  const redemptions = redemptionsData?.docs || [];

  const handleExport = async () => {
    try {
      await promoCodeService.exportRedemptions({
        keyword: debouncedSearch || undefined,
        ...filters,
      });
      toast.success("Redemption history exported successfully!");
    } catch (e) {
      errorHandler(e?.response || e);
    }
  };

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  return (
    <AppLayout>
      <Header
        title="Redemption History"
        description="Promotional discount applications across Jureb checkout and billing"
      />
      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <IconButton className="bg-secondary-subtle mb-3" onClick={handleBack}>
          <ArrowBack />
        </IconButton>

        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
          <div className="d-flex flex-wrap gap-3 flex-grow-1">
            <div style={{ flex: "1 1 260px", maxWidth: 360 }}>
              <AppTextField
                placeholder="Search by customer, email, code..."
                value={searchTerm}
                onChange={handleFilterChange(setSearchTerm)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search className="text-secondary" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div style={{ width: 180 }}>
              <AppSelectField value={selectedPlan} onChange={handleFilterChange(setSelectedPlan)} items={PLAN_FILTER_OPTIONS} />
            </div>
            <div style={{ width: 180 }}>
              <AppSelectField
                value={selectedPlatform}
                onChange={handleFilterChange(setSelectedPlatform)}
                items={PLATFORM_FILTER_OPTIONS}
              />
            </div>
            <div style={{ width: 180 }}>
              <AppSelectField
                value={selectedStatus}
                onChange={handleFilterChange(setSelectedStatus)}
                items={STATUS_FILTER_OPTIONS}
              />
            </div>
          </div>
          <AppExportButton onClick={handleExport} />
        </div>

        <DataTable
          data={redemptions}
          columns={columns}
          isLoading={isLoadingRedemptions}
          page={page}
          pageSize={limit}
          totalPages={redemptionsData?.totalPages || 1}
          onPageChange={setPage}
          onPageSizeChange={(newLimit) => {
            setLimit(newLimit);
            setPage(1);
          }}
          pageSizeOptions={[10, 20, 50]}
        />
      </div>
    </AppLayout>
  );
}
